import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from '../utils/debounce';

type UseSearchReturn = [
    searchText: string,
    isSearching: boolean,
    handleSearchText: (v: string) => void,
];

export function useSearch(
    handleSearch: (v: string) => Promise<void>,
    minLength: number = 3,
    debounceMs: number = 300,
    onBelowMinLength?: () => void,
): UseSearchReturn {
    const [searchText, setSearchText] = useState<string>('');
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const handleSearchRef = useRef(handleSearch);
    handleSearchRef.current = handleSearch;

    const onBelowMinRef = useRef(onBelowMinLength);
    onBelowMinRef.current = onBelowMinLength;

    const prevSearchRef = useRef<string>('');
    const min = Math.max(0, minLength);

    const runSearch = useCallback(async (q: string) => {
        setIsSearching(true);
        try {
            await handleSearchRef.current(q);
        } finally {
            setIsSearching(false);
        }
    }, []);

    const runDebounced = useMemo(
        () => debounce((q: string) => void runSearch(q), Math.max(0, debounceMs)),
        [debounceMs, runSearch],
    );

    useEffect(() => {
        if (onBelowMinRef.current) {
            const wasNarrowing = prevSearchRef.current.length >= min;
            const isBelowMin = searchText.length < min;
            if (wasNarrowing && isBelowMin) {
                onBelowMinRef.current();
            }
        }
        prevSearchRef.current = searchText;
    }, [searchText, min]);

    useEffect(() => {
        if (searchText.length < minLength) {
            setIsSearching(false);
            return;
        }

        if (debounceMs <= 0) {
            void runSearch(searchText);
            return;
        }

        runDebounced(searchText);
        return () => runDebounced.cancel();
    }, [searchText, minLength, debounceMs, runSearch, runDebounced]);

    return [searchText, isSearching, (v: string) => setSearchText(v)];
}
